import { useEffect, useState } from 'react'

import { Attribute, ScriptStatus, EventTypes } from './use-scripts.types'

export type ScriptElement = HTMLScriptElement | null

const createScriptElement = (src: string, params?: { [key: string]: unknown }) => {
  const script: ScriptElement = document.createElement('script')
  script.src = src
  script.async = true
  script.setAttribute(Attribute.DataStatus, ScriptStatus.Loading)

  if (params) {
    Object.keys(params).forEach((key) => {
      script[key] = params[key]
    })
  }

  return script
}

const useScript = (src?: string, scriptCustomParams?: { [key: string]: unknown }) => {
  const [status, setStatus] = useState<ScriptStatus>(src ? ScriptStatus.Loading : ScriptStatus.Idle)

  useEffect(() => {
    if (!src) {
      setStatus(ScriptStatus.Idle)

      return
    }

    let script: ScriptElement = document.querySelector(`script[src="${src}"]`)

    if (!script) {
      script = createScriptElement(src, scriptCustomParams)

      document.body.appendChild(script)

      const setAttributeFromEvent = (event: Event) => {
        script?.setAttribute(
          Attribute.DataStatus,
          event.type === EventTypes.Load ? ScriptStatus.Ready : ScriptStatus.Error
        )
      }

      script.addEventListener(EventTypes.Load, setAttributeFromEvent)
      script.addEventListener(EventTypes.Error, setAttributeFromEvent)
    } else {
      setStatus(script.getAttribute(Attribute.DataStatus) as ScriptStatus)
    }

    const setStateFromEvent = (event: Event) => {
      setStatus(event.type === EventTypes.Load ? ScriptStatus.Ready : ScriptStatus.Error)
    }

    script.addEventListener(EventTypes.Load, setStateFromEvent)
    script.addEventListener(EventTypes.Error, setStateFromEvent)

    return () => {
      if (script) {
        script.removeEventListener(EventTypes.Load, setStateFromEvent)
        script.removeEventListener(EventTypes.Error, setStateFromEvent)
      }
    }
  }, [src, scriptCustomParams])

  return status
}

export default useScript
